import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import apiClient from '@/services/apiClient';
import { HistoryRow, HistoryCard } from './HistoryRow';

const PAGE_SIZE = 25;

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-lg border border-[#E5DFD0] px-4 py-3" data-testid={testid}>
    <p className="text-xs text-[#5A5F6E]">{label}</p>
    <p className="text-2xl font-bold text-[#1A1F2E]">{(value ?? 0).toLocaleString()}</p>
  </div>
);

export const ClearanceHistory = () => {
  const [filters, setFilters] = useState({ search: '', from: '', to: '' });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (applied.search) params.append('search', applied.search);
      if (applied.from) params.append('date_from', applied.from);
      if (applied.to) params.append('date_to', applied.to);
      const res = await apiClient.get(`/wms/sfg/floor-clearance/history?${params.toString()}`);
      setData(res.data);
    } catch {
      setData({ rows: [], total: 0, summary: {} });
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => { load(); }, [load]);

  const apply = (e) => { e.preventDefault(); setPage(1); setApplied(filters); };
  const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-4" data-testid="fc-history">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Bundles re-bundled" value={data?.summary?.bundles} testid="fc-history-bundles" />
        <Stat label="Pieces cleared" value={data?.summary?.pieces} testid="fc-history-pieces" />
        <Stat label="Waiting for put-away" value={data?.summary?.pending_put_away} testid="fc-history-pending" />
      </div>
      <form onSubmit={apply} className="flex flex-wrap items-end gap-2 bg-white rounded-xl border border-[#E5DFD0] p-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-[#5A5F6E]">Search</label>
          <Input value={filters.search} onChange={set('search')} placeholder="Bundle, SKU, design or USN" data-testid="fc-history-search" />
        </div>
        <div><label className="text-xs text-[#5A5F6E]">From</label><Input type="date" value={filters.from} onChange={set('from')} data-testid="fc-history-from" /></div>
        <div><label className="text-xs text-[#5A5F6E]">To</label><Input type="date" value={filters.to} onChange={set('to')} data-testid="fc-history-to" /></div>
        <Button type="submit" className="bg-[#1A1F2E]" data-testid="fc-history-apply"><Search className="h-4 w-4 mr-1" /> Apply</Button>
      </form>
      <div className="md:hidden space-y-2" data-testid="fc-history-cards">
        {!loading && data?.rows?.map((r) => <HistoryCard key={r.clearance_id} row={r} />)}
      </div>
      <div className="hidden md:block bg-white rounded-xl border border-[#E5DFD0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFE6] text-[#5A5F6E] text-xs uppercase tracking-wide">
            <tr>{['Date', 'New bundle', 'Item', 'Pieces', 'From bundles', 'By', 'Status'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-gray-400" /></td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={7} className="py-10 text-center text-[#5A5F6E]" data-testid="fc-history-empty">No floor clearance bundles yet.</td></tr>
            ) : data.rows.map((r) => <HistoryRow key={r.clearance_id} row={r} />)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2 text-sm">
        <span className="text-[#5A5F6E]" data-testid="fc-history-page">Page {page} of {pages}</span>
        <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="fc-history-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="fc-history-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};
